import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { EditOutlined } from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from 'app/state'
import BuilderTakeOverRequestDialog from 'features/builder/ui/Collaboration/BuilderTakeOverRequestDialog'
import BuilderTakeOverResponseDialog from 'features/builder/ui/Collaboration/BuilderTakeOverResponseDialog'
import { setTakeOverResponseState } from 'features/builder/state/builderSlice'
import { useCurrentCollaborativeMapInfo } from 'features/builder/hooks/collaboration'
import { AlertSeverity, showNotification } from 'features/notifications/state/notificationSlice'
import { BuilderMode } from 'features/builder/types'
import TakeOverCanceledDialog from 'features/collaboration/ui/takeOver/TakeOverCanceledDialog'
import { navigateWithReload } from 'features/common/utils/navigationUtils'
import HeaderButton from 'features/common/ui/AppTemplate/Header/Partials/HeaderButton'
import { BuilderPaths } from 'features/builder/paths'
import { CollaborativeResourceType, ResourceType } from 'features/common'
import { TakeOverResult, TakeResponseDialogResult } from 'features/collaboration/types'
import {
  approvedNotification,
  notificationDenied,
  switchedToViewerNotification
} from 'features/collaboration/ui/takeOver/TakeOverNotifications'

export default function BuilderTakeover() {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const [voluntaryOpen, setVoluntaryOpen] = useState(false)
  const builderMode = useAppSelector((state) => state.builder.builderMode)
  const previewMode = useAppSelector((state) => state.builder.previewMode)
  const takeOverResponseState = useAppSelector((state) => state.builder.takeOverResponseState)
  const { map, userHasEditMapPermission, takeOverNeeded, locked, lockedByEmail, lockedByMe } =
    useCurrentCollaborativeMapInfo()
  const [takeoverCanceledOpen, setTakeoverCanceledOpen] = useState(false)
  const mapId = map?.id
  const collaborative = map?.collaborative
  const inViewerMode = builderMode === BuilderMode.Viewer && !previewMode

  useEffect(() => {
    // we lost lock because of timeout forced takeover
    if (mapId && takeOverResponseState?.active && !lockedByMe) {
      navigateWithReload(BuilderPaths.ViewMap(mapId), { notify: switchedToViewerNotification(intl, ResourceType.Map) })
    }
  }, [locked, lockedByMe, takeOverResponseState, mapId, intl])

  const forcedOpen = !inViewerMode && takeOverNeeded

  const handleTakeOverResult = (result: TakeOverResult) => {
    if (!mapId) return

    switch (result) {
      case 'denied': {
        const notification = notificationDenied(intl, lockedByEmail || '')

        if (!forcedOpen) {
          setVoluntaryOpen(false)
          dispatch(showNotification(notification))
        } else {
          navigateWithReload(BuilderPaths.ViewMap(mapId), {
            notify: notification
          })
        }
        break
      }
      case 'cancel':
        if (forcedOpen) {
          navigateWithReload(BuilderPaths.ViewMap(mapId))
        } else {
          setVoluntaryOpen(false)
        }
        break
      case 'locked': {
        if (inViewerMode) {
          navigateWithReload(BuilderPaths.EditMap(mapId), {
            notify: approvedNotification(intl, ResourceType.Map)
          })
        } else {
          dispatch(showNotification(approvedNotification(intl, ResourceType.Map)))
        }
        break
      }
      case 'conflict':
        // someone else managed to lock map, just notify and keep open
        dispatch(
          showNotification({
            type: AlertSeverity.Info,
            msg: 'Someone else locked the map',
            detailsMsg: 'You may request takeover again',
            formatted: false
          })
        )
    }
  }

  const hadleClickTakeOver = () => {
    if (takeOverNeeded) {
      setVoluntaryOpen(true)
    } else if (mapId) {
      navigateWithReload(BuilderPaths.EditMap(mapId))
    }
  }

  const requestingTakeoverOpen = voluntaryOpen || forcedOpen
  const acceptingTakeoverOpen = !requestingTakeoverOpen && takeOverResponseState?.active

  const handleTakeoverResponse = (result: TakeResponseDialogResult) => {
    if (!mapId) return
    if (result === 'accepted') {
      navigateWithReload(BuilderPaths.ViewMap(mapId), {
        notify: switchedToViewerNotification(intl, ResourceType.Map)
      })
    } else if (result === 'canceled') {
      setTakeoverCanceledOpen(true)
      dispatch(setTakeOverResponseState({ ...takeOverResponseState, active: false }))
    } else {
      dispatch(setTakeOverResponseState({ active: false }))
    }
  }

  const editOrtakeOverButtonVisible = userHasEditMapPermission && inViewerMode

  return (
    <>
      {editOrtakeOverButtonVisible && (
        <HeaderButton
          onClick={hadleClickTakeOver}
          startIcon={<EditOutlined />}
          aria-label={takeOverNeeded ? 'takeover-editing' : 'edit-map'}
        >
          <FormattedMessage id={takeOverNeeded ? 'builder.navBar.takeOver' : 'builder.actions.edit'} />
        </HeaderButton>
      )}

      {collaborative && (
        <>
          <BuilderTakeOverRequestDialog
            open={requestingTakeoverOpen}
            mode={forcedOpen ? 'forced' : 'userRequest'}
            onFinished={handleTakeOverResult}
          />
          <BuilderTakeOverResponseDialog open={acceptingTakeoverOpen} onFinished={handleTakeoverResponse} />
          <TakeOverCanceledDialog
            open={takeoverCanceledOpen}
            onClose={() => setTakeoverCanceledOpen(false)}
            userEmail={takeOverResponseState?.email || ''}
            resourceType={CollaborativeResourceType.Map}
          />
        </>
      )}
    </>
  )
}
